import { createContext, useCallback, useMemo, type ReactNode } from 'react';
import type { Session, User as SupabaseUser, Provider, UserIdentity } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { i18n } from '@pantry/i18n';
import { clearAllCaches } from '../lib/queryClient';
import { AnalyticsEvents } from '@pantry/analytics';
import { posthog, isPostHogAvailable } from '../lib/posthog';
import { useAuthCore } from '@pantry/shared-react/auth';

interface AuthContextType {
  session: Session | null;
  user: SupabaseUser | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithMagicLink: (email: string, postAuthRedirect?: string) => Promise<void>;
  signInWithOAuth: (provider: Provider, postAuthRedirect?: string) => Promise<void>;
  signUp: (email: string, password: string, name?: string) => Promise<void>;
  signOut: () => Promise<void>;
  getToken: () => Promise<string | null>;
  updatePassword: (newPassword: string) => Promise<void>;
  updateEmail: (newEmail: string) => Promise<void>;
  resetPasswordForEmail: (email: string) => Promise<void>;
  resendConfirmation: (email: string) => Promise<void>;
  // Account Linking
  getLinkedIdentities: () => Promise<UserIdentity[]>;
  linkProvider: (provider: Provider) => Promise<void>;
  unlinkProvider: (identity: UserIdentity) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const core = useAuthCore({
    supabaseClient: supabase,
    onSessionExpired: () => {
      toast.error(
        i18n.t({
          id: 'session.expired.message',
          message: 'Your session has expired. Please sign in again.',
        })
      );
      window.location.href = '/login';
    },
    clearCaches: () => {
      clearAllCaches();
    },
    onPasswordRecovery: () => {
      window.location.href = '/reset-password';
    },
  });

  const signInWithMagicLink = useCallback(async (email: string, postAuthRedirect?: string) => {
    const redirectTo = postAuthRedirect
      ? `${window.location.origin}${postAuthRedirect}`
      : `${window.location.origin}/dashboard`;
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectTo,
      },
    });
    if (error) throw error;
  }, []);

  const signInWithOAuth = useCallback(async (provider: Provider, postAuthRedirect?: string) => {
    // Track OAuth start to prevent bounce counting during redirect
    if (isPostHogAvailable()) {
      posthog.capture(AnalyticsEvents.AUTH_OAUTH_STARTED, {
        provider,
        flow: 'sign_in',
      });
    }

    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: postAuthRedirect
          ? `${window.location.origin}/auth-callback?redirect=${encodeURIComponent(postAuthRedirect)}`
          : `${window.location.origin}/auth-callback`,
      },
    });
    if (error) throw error;
  }, []);

  const resetPasswordForEmail = useCallback(async (email: string) => {
    const redirectTo = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });
    if (error) throw error;
  }, []);

  const linkProvider = useCallback(async (provider: Provider) => {
    // Track OAuth start to prevent bounce counting during redirect
    if (isPostHogAvailable()) {
      posthog.capture(AnalyticsEvents.AUTH_OAUTH_STARTED, {
        provider,
        flow: 'link_account',
      });
    }

    const { error } = await supabase.auth.linkIdentity({
      provider,
      options: {
        redirectTo: `${window.location.origin}/settings?linked=${provider}`,
      },
    });
    if (error) throw error;
  }, []);

  const value = useMemo<AuthContextType>(
    () => ({
      ...core,
      signInWithMagicLink,
      signInWithOAuth,
      resetPasswordForEmail,
      linkProvider,
    }),
    [core, signInWithMagicLink, signInWithOAuth, resetPasswordForEmail, linkProvider]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
